import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { BudgetRequestStatus } from 'src/models/budgetRequest.models'
import { CreditCardType } from 'src/models/creditCard.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { createBudgetUrl } from 'src/navigation/urls'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { handlePageLocation } from 'src/utils/analytics.utils'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface UpdateBudgetRequestStandardContentProps {
  recipient: UserListModel
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions: CreditCardOption[]
  puttingBudgetRequest: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  initialFlatFields?: Partial<BudgetFlatFormValues>
  setScreen: (val: BudgetScreen) => void
  putBudgetRequest: (
    body: Partial<PostCreditCardRequest>,
    status: BudgetRequestStatus,
  ) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isFormDisabled: boolean
  requesting?: boolean
  approving?: boolean
  approver: UserListModel
  showCurrencyError: boolean
}

const UpdateBudgetRequestStandardContent: React.FC<
  UpdateBudgetRequestStandardContentProps
> = (props) => {
  const { analytics } = useAnalyticsContext()
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const user = useSelector(selectUserListModelFromState)
  const budgetStandardActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetStandardActive,
  )
  const showRequestBudget = props.requesting && !props.approving
  const showApproveBudgetRequest = props.approving

  const onSuccessfulSubmit = (
    flatFields: BudgetFlatFormValues,
    recurrenceFields: RecurrenceFormValues,
    status: BudgetRequestStatus,
  ): void => {
    const budgetRequestBodyRejected = {
      approverEmail: props.approver.email,
      currency: flatFields.currency,
      emails: flatFields.emails,
      displayName: flatFields.displayName,
      issuingLimitCents:
        flatFields.issuingLimitDollars !== undefined && !flatFields.isUnlimited
          ? toCents(flatFields.issuingLimitDollars)
          : undefined,
      until:
        flatFields.untilMoment !== undefined && !flatFields.isForever
          ? flatFields.untilMoment.format('YYYY-MM-DD')
          : undefined,
      type: CreditCardType.Delegate,
      notes: flatFields.notes,
      recurs: false,
      recurrence: undefined,
    }
    const budgetRequestBody = {
      ...budgetRequestBodyRejected,
      parentCreditCardId: flatFields.creditCardId,
    }
    props.putBudgetRequest(
      status === BudgetRequestStatus.Rejected
        ? budgetRequestBodyRejected
        : budgetRequestBody,
      status,
    )
    /* Old analytics event - tbd if / when we delete */
    if (user !== undefined) {
      analytics?.track(
        handlePageLocation(calculatedBackgroundLocation) +
          ' - create standard budget - button press',
        {
          interaction: 'button press',
          intent: 'create a budget',
          typeOfBudget: 'Standard',
          budgetName: flatFields.displayName,
          recipientCount:
            flatFields.emails !== undefined
              ? flatFields.emails.length
              : flatFields.email !== undefined
                ? 1
                : 0,
          recipient:
            flatFields.emails !== undefined
              ? flatFields.emails
              : flatFields.email !== undefined
                ? flatFields.email
                : undefined,
          isRecipientMe: flatFields.emails
            ? flatFields.emails.includes(user.email)
            : flatFields.email !== undefined
              ? flatFields.email === user.email
              : undefined,
          sourceAccount:
            flatFields.creditCardId !== undefined
              ? creditCards[flatFields.creditCardId].displayName
              : undefined,
          budgetLimit: flatFields.isUnlimited
            ? 'Unlimited'
            : '$' + flatFields.issuingLimitDollars,
          activeUntil: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment,
        },
      )

      analytics?.track(
        'create budget flyout - successfully create a budget - button press',
        {
          interaction: 'button press',
          intent: 'successfully create a budget',
          screen: createBudgetUrl(),
          typeOfBudget: 'Standard',
          budgetName: flatFields.displayName,
          recipientCount:
            flatFields.emails !== undefined
              ? flatFields.emails.length
              : flatFields.email !== undefined
                ? 1
                : 0,
          recipient:
            flatFields.emails !== undefined
              ? flatFields.emails
              : flatFields.email !== undefined
                ? flatFields.email
                : undefined,
          isRecipientMe: flatFields.emails
            ? flatFields.emails.includes(user.email)
            : flatFields.email !== undefined
              ? flatFields.email === user.email
              : undefined,
          sourceAccount:
            flatFields.creditCardId !== undefined
              ? creditCards[flatFields.creditCardId].displayName
              : undefined,
          budgetLimit: flatFields.isUnlimited
            ? 'Unlimited'
            : '$' + flatFields.issuingLimitDollars,
          activeUntil: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment,
        },
      )
    }
  }

  return (
    <ActionRightFlyoutContent
      title={
        <Trns
          source={
            showRequestBudget
              ? 'Request Budget Update'
              : showApproveBudgetRequest
                ? 'Approve Budget Request'
                : 'Create a Budget'
          }
        />
      }
      specificElements={{
        subtitle: <Trns source={'Standard'} />,
        imageSrc: budgetStandardActiveImage,
        changeLink: {
          ariaLabelSource: 'Change Budget Type',
          changeMessage: <Trns source={'Change Budget Type'} />,
          onChangeClick: (): void => {
            props.setScreen(BudgetScreen.Pick)
          },
        },
      }}
      permissions={{
        isBudget: true,
      }}
    >
      <BudgetForm
        buttonText={
          <Trns
            source={
              showRequestBudget
                ? 'Request Update'
                : showApproveBudgetRequest
                  ? 'Approve Budget Request'
                  : 'Create a Budget'
            }
          />
        }
        topText={
          <Trns
            source={
              'Requests require approval before the budget can be used. While awaiting for approval, you can update your request.'
            }
          />
        }
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.puttingBudgetRequest}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={
          showRequestBudget ? (
            <Trns source={'Request from'} />
          ) : (
            <Trns source={'Recipient'} />
          )
        }
        recurrenceEditable={false}
        rcnOptions={props.rcnOptions}
        initialFlatFields={props.initialFlatFields}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        isFormDisabled={props.isFormDisabled}
        suggestedProps={{
          showSuggested: user?.hasOrgAdminPermissions,
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.delegateCreditCardTo]}
        unableToAddNewUser={!user?.hasOrgAdminPermissions}
        isRequestBudget={showRequestBudget}
        isUpdateRequest={showRequestBudget}
        isApproveRequest={props.approving}
        approver={props.approver}
        currencyError={props.showCurrencyError}
      />
    </ActionRightFlyoutContent>
  )
}

export default UpdateBudgetRequestStandardContent
